import { FC, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { Box, debounce } from '@mui/material';

import { UserTable } from './UserTable';
import { metaTitle } from '../../../util/tabTitle';
import { UserRole } from '../../../types/user.types';
import { useAuth } from '../../../context/auth-context';
import { AddUserModal } from './components/AddUserModal';
import Breadcrumbs from '../../../components/Breadcrumb';
import { DEBOUNCE_TIME } from '../../../configs';

import {
  ManagementPageHeader,
  ManagementSearch,
  ManagementToolbar,
} from '../../../components/ManagementPageLayout';

const UserPage: FC = () => {
  metaTitle('Super Admin - Users');

  const params = useParams<{ id: string }>();
  const { currentUser } = useAuth();

  const [openUserModal, setOpenUserModal] = useState(false);
  const [refreshUserTable, setRefreshUserTable] = useState(false);
  const [search, setSearch] = useState('');
  const [searchText, setSearchText] = useState('');
  const { state } = useLocation();

  if (currentUser && currentUser.role !== UserRole.superAdmin) {
    return <Navigate to="/" />;
  }

  const searchParams = state?.search;
  const organizationPageUrl = searchParams
    ? `/admin/organizations${searchParams}`
    : '/admin/organizations';

  return (
    <Box sx={{ width: '100%', maxWidth: 1360, mx: 'auto' }}>
      <Breadcrumbs
        items={[
          { label: 'Organizations', to: organizationPageUrl },
          { label: 'Admin Users', to: `/admin/users/${params.id}` },
        ]}
      />
      <ManagementPageHeader
        title="Organization users"
        subtitle="Manage users and account access for this organization."
        actionLabel="Add user"
        onAction={() => setOpenUserModal(true)}
      />
      <ManagementToolbar>
        <ManagementSearch
          placeholder="Search users"
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
            debounce(() => setSearch(event.target.value), DEBOUNCE_TIME)();
          }}
        />
        <Box />
      </ManagementToolbar>

      <UserTable
        selectedOrgId={params?.id || ''}
        search={search}
        refresh={refreshUserTable}
      />
      <AddUserModal
        onClose={() => setOpenUserModal(false)}
        open={openUserModal}
        onFinished={() => {
          setOpenUserModal(false);
          setRefreshUserTable(!refreshUserTable);
        }}
      />
    </Box>
  );
};

export default UserPage;
